import { useState, useEffect, useCallback, useRef } from 'react';
import { logger } from '@/lib/logger';
import { Input } from '@/components/ui/input';
import { Search, AlertCircle } from 'lucide-react';
import { CleanProposalList } from './CleanProposalList';
import { createPortalioClient } from '@/services/portalio/client';
import { toast } from 'sonner';
import { useDebounce } from '@/hooks/useDebounce';

interface Proposal {
  id: number;
  proposalName: string;
  proposalNumber: number;
  customerName: string;
  status: string;
  totalAmount: number;
  createdDate: string;
}

interface PortalProposalSelectorProps {
  organizationId: string;
  onSelectProposal: (proposal: Proposal) => void;
}

export function PortalProposalSelector({
  organizationId,
  onSelectProposal,
}: PortalProposalSelectorProps) {
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [isSearching, setIsSearching] = useState(false);

  const clientRef = useRef<ReturnType<typeof createPortalioClient>>();
  const loadingRef = useRef(false);
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Initialize client
  useEffect(() => {
    if (organizationId) {
      clientRef.current = createPortalioClient(organizationId);
    }
  }, [organizationId]);

  // Load proposals
  const loadProposals = useCallback(
    async (search: string = '', pageNum: number = 1, append: boolean = false) => {
      if (!clientRef.current || loadingRef.current) return;

      loadingRef.current = true;
      setLoading(true);

      try {
        logger.log(`Loading proposals: search="${search}", page=${pageNum}`);

        const result = await clientRef.current.getProposals(
          search || undefined,
          pageNum,
          20, // Page size for optimal performance
        );

        if (result.proposals) {
          if (append) {
            setProposals(prev => [...prev, ...result.proposals]);
          } else {
            setProposals(result.proposals);
          }

          setTotalCount(result.total || 0);
          // Check if we have more proposals to load
          const currentTotal = append
            ? proposals.length + result.proposals.length
            : result.proposals.length;
          setHasMore(currentTotal < (result.total || 0));

          logger.log(`Loaded ${result.proposals.length} proposals, total: ${result.total}`);
        }
      } catch (error) {
        logger.error('Error loading proposals:', error);
        toast.error('Failed to load proposals');
      } finally {
        setLoading(false);
        loadingRef.current = false;
        setIsSearching(false);
      }
    },
    [proposals.length],
  );

  // Initial load
  useEffect(() => {
    loadProposals('', 1, false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle search
  useEffect(() => {
    if (debouncedSearch !== undefined) {
      logger.log('Searching for:', debouncedSearch);
      setIsSearching(true);
      setPage(1);
      setProposals([]);
      setHasMore(true);
      loadProposals(debouncedSearch, 1, false);
    }
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load more handler
  const handleLoadMore = useCallback(() => {
    if (!hasMore || loading) return;

    const nextPage = page + 1;
    setPage(nextPage);
    loadProposals(debouncedSearch, nextPage, true);
  }, [hasMore, loading, page, debouncedSearch, loadProposals]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Search Bar */}
      <div className="p-4 border-b flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search proposals..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => e.stopPropagation()}
            onKeyUp={e => e.stopPropagation()}
            className="pl-10 h-12 text-base" // Larger for touch
            autoFocus
          />
          {isSearching && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>

        {/* Results count */}
        {!loading && proposals.length > 0 && (
          <p className="text-xs text-muted-foreground mt-2">
            Showing {proposals.length} of {totalCount} proposals
          </p>
        )}
      </div>

      {/* Proposals List */}
      <div className="flex-1 overflow-auto">
        {proposals.length === 0 && !loading ? (
          <div className="flex flex-col items-center justify-center h-full p-8">
            <AlertCircle className="h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-muted-foreground text-center">
              {searchQuery ? `No proposals found for "${searchQuery}"` : 'No proposals found'}
            </p>
            <p className="text-sm text-muted-foreground mt-1 text-center">
              {searchQuery
                ? 'Try adjusting your search terms'
                : 'Create proposals in Portal.io to import products'}
            </p>
          </div>
        ) : (
          <CleanProposalList
            proposals={proposals}
            loading={loading}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            onSelectProposal={onSelectProposal}
          />
        )}
      </div>
    </div>
  );
}

// Custom debounce hook if not already available
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
